/**
 * PaymentLinkDetailDialog - Dialog de detalhes do link de pagamento
 * 
 * Exibe KPIs premium e tabela de cobranças associadas ao link,
 * buscando dados locais via RPC get_cobrancas_paginated com filtro provider_payment_link.
 * Inclui colunas de ações do provider e menu de ações (ver detalhes, WhatsApp).
 * 
 * RELEASE: 2026-03-21 v1.0.0 - Criação do componente
 * RELEASE: 2026-03-22 v2.0.0 - Refatoração: dados locais via RPC, KPIs premium, tabela idêntica à listagem
 * RELEASE: 2026-03-22 v2.1.0 - Adição de colunas de ações (provider + menu dropdown)
 * @author Módulo Financeiro - Links de Pagamento
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Receipt,
  CheckCircle2,
  Clock,
  MoreHorizontal,
  Eye,
  MessageCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { parseDateLocal } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { type PaymentLink } from '@/hooks/useFinancialPaymentLinks';
import { useFinanceiroCobrancasPaginated } from '@/hooks/useFinanceiroCobrancasPaginated';
import { FinanceiroStatus, type FinanceiroCobranca } from '@/hooks/useFinanceiroCobrancas';
import { TIPOS_PAGAMENTO } from '@/hooks/useFinanceiroMeiosPagamento';
import { ChargeProviderActions } from '@/components/financial/ChargeProviderActions';
import { ChargeDetailsModal } from '@/components/financial/ChargeDetailsModal';
// v1.0.0 (2026-06-21): resolução com contingência checkout transparente x provider.
import { resolveCheckoutUrl } from '@/lib/financial/resolveCheckoutUrl';

// ============= TIPOS E CONSTANTES =============

interface PaymentLinkDetailDialogProps {
  open: boolean;
  onClose: () => void;
  link: PaymentLink | null;
  contaCorrenteId: string;
}

/**
 * Mapa de cores para badge de status (idêntico ao FinancialCharges)
 */
const statusBadgeColors: Record<string, string> = {
  pendente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  edicao_andamento: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  aguardando_pagamento: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  pago: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  parcial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  vencido: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  cancelado: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

/** Labels de status para exibição */
const statusLabels: Record<string, string> = {
  pendente: 'Pendente',
  edicao_andamento: 'Edição em andamento',
  aguardando_pagamento: 'Aguardando Pagamento',
  pago: 'Pago',
  parcial: 'Parcial',
  vencido: 'Vencido',
  cancelado: 'Cancelado',
};

/** Labels de tipo de cobrança */
const tipoLabels: Record<string, string> = {
  unica: 'Única',
  parcelada: 'Parcelada',
  recorrente: 'Recorrente',
};

/**
 * Cor da barra lateral por status (idêntico ao FinancialCharges)
 */
function getStatusBarColor(status: string): string {
  switch (status) {
    case 'pendente': return 'bg-yellow-500';
    case 'edicao_andamento': return 'bg-amber-500';
    case 'pago': return 'bg-green-500';
    case 'parcial': return 'bg-blue-500';
    case 'vencido': return 'bg-red-500';
    case 'cancelado': return 'bg-muted-foreground/40';
    case 'aguardando_pagamento': return 'bg-orange-500';
    default: return 'bg-muted-foreground/20';
  }
}

/**
 * Formata valor em BRL
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

// ============= COMPONENTE PRINCIPAL =============

export function PaymentLinkDetailDialog({
  open,
  onClose,
  link,
  contaCorrenteId,
}: PaymentLinkDetailDialogProps) {

  /** Estado para modal de detalhes da cobrança selecionada */
  const [detailCobranca, setDetailCobranca] = useState<FinanceiroCobranca | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Busca cobranças locais filtradas pelo provider_payment_link do link
  const {
    cobrancas,
    totalCount,
    kpis,
    isLoading,
  } = useFinanceiroCobrancasPaginated(
    open && link?.provider_link_id
      ? { provider_payment_link: link.provider_link_id }
      : undefined,
    { page: 1, pageSize: 200 }
  );

  /**
   * Abre WhatsApp com link de pagamento da cobrança
   */
  const handleWhatsApp = (cobranca: any) => {
    const paymentUrl = resolveCheckoutUrl({
      checkout_mode: cobranca.checkout_mode,
      checkout_session_slug: cobranca.checkout_session_slug,
      provider_invoice_url: cobranca.provider_invoice_url,
      provider_payment_link: cobranca.provider_payment_link,
    }).url;
    if (paymentUrl) {
      const text = encodeURIComponent(`Olá! Segue o link para pagamento: ${paymentUrl}`);
      window.open(`https://wa.me/?text=${text}`, '_blank');
    }
  };

  if (!link) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* ====== Área fixa: Header + KPIs (sem scroll) ====== */}
        <div className="flex-shrink-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="pb-4 border-b border-border/40">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
                {link.name}
                {link.deleted ? (
                  <Badge variant="destructive" className="text-xs">Removido</Badge>
                ) : link.active ? (
                  <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/20 text-xs">Ativo</Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">Inativo</Badge>
                )}
              </DialogTitle>
              {link.description && (
                <p className="text-sm text-muted-foreground mt-1">{link.description}</p>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* ====== KPI Cards Premium (padrão FinancialCharges) ====== */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          {/* Total Cobranças */}
          <Card className="relative overflow-hidden border-border/50 bg-gradient-to-br from-background to-muted/10 p-4 group hover:shadow-md transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <div className="relative flex items-center gap-3">
              <div className={cn('p-2.5 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 text-primary')}>
                <Receipt className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Total Cobranças</p>
                {isLoading ? (
                  <Skeleton className="h-7 w-12 mt-0.5" />
                ) : (
                  <p className="text-2xl font-bold text-foreground">{totalCount}</p>
                )}
              </div>
            </div>
          </Card>

          {/* Faturas Pagas */}
          <Card className="relative overflow-hidden border-border/50 bg-gradient-to-br from-background to-muted/10 p-4 group hover:shadow-md transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <div className="relative flex items-center gap-3">
              <div className={cn('p-2.5 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 text-emerald-600 dark:text-emerald-400')}>
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Faturas Pagas</p>
                {isLoading ? (
                  <Skeleton className="h-7 w-20 mt-0.5" />
                ) : (
                  <p className="text-2xl font-bold text-foreground">
                    {kpis.totalPagas}/{kpis.totalFaturas}
                    <span className="text-xs font-normal text-muted-foreground ml-1">pagas</span>
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Recebido */}
          <Card className="relative overflow-hidden border-border/50 bg-gradient-to-br from-background to-muted/10 p-4 group hover:shadow-md transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <div className="relative flex items-center gap-3">
              <div className={cn('p-2.5 rounded-xl bg-gradient-to-br from-sky-500/10 to-sky-500/5 text-sky-600 dark:text-sky-400')}>
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Recebido</p>
                {isLoading ? (
                  <Skeleton className="h-7 w-24 mt-0.5" />
                ) : (
                  <p className="text-2xl font-bold text-foreground">{formatCurrency(kpis.recebido)}</p>
                )}
              </div>
            </div>
          </Card>

          {/* Previsto / A Receber */}
          <Card className="relative overflow-hidden border-border/50 bg-gradient-to-br from-background to-muted/10 p-4 group hover:shadow-md transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <div className="relative flex items-center gap-3">
              <div className={cn('p-2.5 rounded-xl bg-gradient-to-br from-violet-500/10 to-violet-500/5 text-violet-600 dark:text-violet-400')}>
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Previsto</p>
                {isLoading ? (
                  <Skeleton className="h-7 w-24 mt-0.5" />
                ) : (
                  <p className="text-2xl font-bold text-foreground">{formatCurrency(kpis.previsto)}</p>
                )}
              </div>
            </div>
           </Card>
        </div>
        </div>{/* Fim da área fixa */}

        {/* ====== Tabela de cobranças - scroll independente (horizontal e vertical) ====== */}
        <div className="flex-1 min-h-0 mt-4 rounded-md border overflow-auto">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : cobrancas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Receipt className="h-8 w-8 opacity-30 mb-2" />
              <p className="text-sm font-medium">Nenhuma cobrança gerada</p>
              <p className="text-xs">As cobranças aparecerão aqui quando clientes realizarem pagamentos</p>
            </div>
          ) : (
            <>
              <Table className="min-w-[1300px]">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    {/* Barra colorida de status */}
                    <TableHead className="w-2 p-0" />
                    <TableHead className="text-xs font-medium">Cliente</TableHead>
                    <TableHead className="text-xs font-medium">Descrição</TableHead>
                    <TableHead className="text-xs font-medium">Tipo</TableHead>
                    <TableHead className="text-xs font-medium">Valor</TableHead>
                    {/* v1.6.0: Colunas de progresso de pagamento */}
                    <TableHead className="text-xs font-medium">Vlr. Restante</TableHead>
                    <TableHead className="text-xs font-medium w-[120px]">% Pago</TableHead>
                    <TableHead className="text-xs font-medium">Status</TableHead>
                    <TableHead className="text-xs font-medium">Vencimento</TableHead>
                    <TableHead className="text-xs font-medium">Dt. Pagamento</TableHead>
                    <TableHead className="text-xs font-medium">Parcelas</TableHead>
                    <TableHead className="text-xs font-medium">Meio Pagamento</TableHead>
                    <TableHead className="text-xs font-medium">Conta Corrente</TableHead>
                    {/* Coluna ações provider */}
                    <TableHead className="w-[80px] p-0" />
                    {/* Coluna menu ações */}
                    <TableHead className="w-[40px] p-0" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cobrancas.map(cobranca => (
                    <TableRow 
                      key={cobranca.id} 
                      className="group hover:bg-muted/40 cursor-pointer"
                      onClick={() => {
                        setDetailCobranca(cobranca as unknown as FinanceiroCobranca);
                        setDetailsOpen(true);
                      }}
                    >
                      {/* Barra lateral colorida */}
                      <TableCell className="p-0 w-2">
                        <div className={cn(
                          'w-1 h-full min-h-[40px] rounded-full',
                          getStatusBarColor(cobranca.status)
                        )} />
                      </TableCell>

                      {/* Cliente + CPF/CNPJ */}
                      <TableCell className="py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate max-w-[200px]">
                            {cobranca.customer_name || 'Sem cliente'}
                          </p>
                          {cobranca.customer_document && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {formatDocumentForDisplay(cobranca.customer_document)}
                            </p>
                          )}
                        </div>
                      </TableCell>

                      {/* Descrição */}
                      <TableCell className="py-2">
                        <p className="text-sm font-medium truncate max-w-[250px]">
                          {cobranca.descricao || 'Sem descrição'}
                        </p>
                      </TableCell>

                      {/* Tipo */}
                      <TableCell className="py-2">
                        <Badge variant="outline" className="text-[11px]">
                          {tipoLabels[cobranca.tipo_cobranca] || cobranca.tipo_cobranca}
                        </Badge>
                      </TableCell>

                      {/* Valor */}
                      <TableCell className="py-2">
                        <span className="text-sm font-mono font-medium">
                          {formatCurrency(cobranca.valor_total)}
                        </span>
                      </TableCell>

                      {/* v1.6.0: Vlr. Restante */}
                      <TableCell className="py-2">
                        {(() => {
                          const recebido = Number(cobranca.valor_recebido || 0);
                          const restante = Math.max(cobranca.valor_total - recebido, 0);
                          const isPago = cobranca.status === 'pago';
                          return (
                            <span className={cn(
                              'text-sm font-mono font-medium',
                              isPago ? 'text-emerald-600' : ''
                            )}>
                              {formatCurrency(isPago ? 0 : restante)}
                            </span>
                          );
                        })()}
                      </TableCell>

                      {/* v1.6.0: % Pago (barra de progresso) */}
                      <TableCell className="py-2">
                        {(() => {
                          const recebido = Number(cobranca.valor_recebido || 0);
                          const percent = cobranca.valor_total > 0
                            ? Math.round((recebido / cobranca.valor_total) * 100)
                            : 0;
                          return (
                            <div className="flex items-center gap-2 min-w-[100px]">
                              <Progress
                                value={percent}
                                className="h-2 flex-1"
                                indicatorClassName={cn(
                                  percent >= 100 ? 'bg-emerald-500' :
                                  percent > 0 ? 'bg-blue-500' :
                                  'bg-muted-foreground/30'
                                )}
                              />
                              <span className="text-[11px] text-muted-foreground w-8 text-right">
                                {percent}%
                              </span>
                            </div>
                          );
                        })()}
                      </TableCell>

                      {/* Status */}
                      <TableCell className="py-2">
                        <span className={cn(
                          'text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
                          statusBadgeColors[cobranca.status] || 'bg-gray-100 text-gray-800'
                        )}>
                          {statusLabels[cobranca.status] || cobranca.status}
                        </span>
                      </TableCell>

                      {/* Vencimento */}
                      <TableCell className="py-2">
                        {cobranca.data_vencimento ? (
                          <span className="text-xs text-muted-foreground">
                            {format(parseDateLocal(cobranca.data_vencimento), 'dd/MM/yy', { locale: ptBR })}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Data Pagamento */}
                      <TableCell className="py-2">
                        {cobranca.primeira_data_pagamento ? (
                          <span className="text-xs text-muted-foreground">
                            {format(parseDateLocal(cobranca.primeira_data_pagamento), 'dd/MM/yy', { locale: ptBR })}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Parcelas */}
                      <TableCell className="py-2">
                        <span className="text-xs text-muted-foreground">
                          {cobranca.numero_parcelas}x
                        </span>
                      </TableCell>

                      {/* Meio de Pagamento */}
                      <TableCell className="py-2">
                        {cobranca.tipo_pagamento ? (
                          <span className="text-xs text-muted-foreground">
                            {TIPOS_PAGAMENTO.find(t => t.value === cobranca.tipo_pagamento)?.label || cobranca.tipo_pagamento}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Conta Corrente */}
                      <TableCell className="py-2">
                        {cobranca.conta_corrente_nome ? (
                          <span className="text-xs text-muted-foreground truncate max-w-[120px] block">
                            {cobranca.conta_corrente_nome}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Ações do Provider (QR Code PIX, Copiar link, Abrir link) */}
                      <TableCell className="py-2" onClick={e => e.stopPropagation()}>
                        {cobranca.provider_charge_id && cobranca.conta_corrente_id &&
                         (cobranca.status === 'aguardando_pagamento' || cobranca.status === 'pago' || cobranca.status === 'parcial') && (
                          <ChargeProviderActions
                            providerChargeId={cobranca.provider_charge_id}
                            contaCorrenteId={cobranca.conta_corrente_id}
                            providerInvoiceUrl={cobranca.provider_invoice_url}
                            providerPaymentLink={cobranca.provider_payment_link}
                            providerSlug={cobranca.provider_slug}
                            status={cobranca.status as FinanceiroStatus}
                            billingType={cobranca.tipo_pagamento}
                            tipoCobranca={cobranca.tipo_cobranca}
                            checkoutMode={cobranca.checkout_mode}
                            checkoutSessionSlug={cobranca.checkout_session_slug}
                          />
                        )}
                      </TableCell>

                      {/* Menu de ações (dropdown) */}
                      <TableCell className="py-2" onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {/* Ver Detalhes */}
                            <DropdownMenuItem onClick={() => {
                              setDetailCobranca(cobranca as unknown as FinanceiroCobranca);
                              setDetailsOpen(true);
                            }}>
                              <Eye className="h-4 w-4 mr-2 text-muted-foreground" />
                              Ver Detalhes
                            </DropdownMenuItem>

                            {/* Enviar via WhatsApp */}
                            {(cobranca.provider_invoice_url || cobranca.provider_payment_link) && (
                              <DropdownMenuItem onClick={() => handleWhatsApp(cobranca)}>
                                <MessageCircle className="h-4 w-4 mr-2 text-emerald-600" />
                                Enviar via WhatsApp
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Rodapé totalizador */}
              <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/20">
                <span className="text-xs text-muted-foreground font-medium">
                  {totalCount} cobrança(s)
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-muted-foreground">
                    Total: <span className="font-mono font-semibold text-foreground">{formatCurrency(kpis.recebido + kpis.previsto)}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Pago: <span className="font-mono font-semibold text-emerald-600">{formatCurrency(kpis.recebido)}</span>
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>

      {/* Modal de detalhes da cobrança */}
      {detailsOpen && (
        <ChargeDetailsModal
          cobranca={detailCobranca}
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
        />
      )}
    </Dialog>
  );
}
